import { GQL } from '@aline/graphql';

import { hubspotFieldIdentityKey } from './hubspot/attribute-path';
import { salesforceFieldIdentityKey } from './salesforce/attribute-path';

const INTEGRATION_TO_SERVICE_ID: Record<GQL.Integration, string> = {
  [GQL.Integration.Act]: 'act',
  [GQL.Integration.ActiveCampaign]: 'activecampaign',
  [GQL.Integration.Close]: 'close',
  [GQL.Integration.Copper]: 'copper',
  [GQL.Integration.HubSpot]: 'hubspot',
  [GQL.Integration.MicrosoftDynamics]: 'microsoft-dynamics',
  [GQL.Integration.Pipedrive]: 'pipedrive',
  [GQL.Integration.Salesflare]: 'salesflare',
  [GQL.Integration.SalesForce]: 'salesforce',
  [GQL.Integration.Teamleader]: 'teamleader',
  [GQL.Integration.ZendeskSell]: 'zendesk-sell',
  [GQL.Integration.ZohoCrm]: 'zoho-crm',
};

/**
 * CRMs Aline links workflows to: publishable from the template builder, offered on the
 * launch form, and each with a `CRM_CATALOG_META` entry and a server field provider.
 * Other Apideck integrations are read-only.
 */
export const LAUNCHABLE_CRMS = [GQL.Integration.HubSpot, GQL.Integration.SalesForce] as const;

export type LaunchableCrm = (typeof LAUNCHABLE_CRMS)[number];

// Shared singletons come back from `getCrmRecordCopy`, so callers must not be able to edit them.
export type CrmRecordCopy = {
  readonly article: 'a' | 'an';
  readonly label: string;
  readonly lower: string;
  readonly plural: string;
};

type CrmCategoryGroup = { readonly displayName: string; readonly sortIndex: number };
type CrmCategoryGroups = Readonly<Record<GQL.CrmIntegrationCategory, CrmCategoryGroup>>;

/**
 * Everything the field catalog needs to know about one CRM: how the picker groups
 * categories, which unified and native paths describe the same field, and the noun
 * for the linked record.
 */
type CrmCatalogMeta = {
  readonly categoryGroups: CrmCategoryGroups;
  readonly recordCopy: CrmRecordCopy;
};

const OPPORTUNITY_RECORD_COPY = {
  article: 'an',
  label: 'Opportunity',
  lower: 'opportunity',
  plural: 'opportunities',
} satisfies CrmRecordCopy;

const DEAL_RECORD_COPY = {
  article: 'a',
  label: 'Deal',
  lower: 'deal',
  plural: 'deals',
} satisfies CrmRecordCopy;

/**
 * What a CRM without a catalog of its own gets: Apideck's unified nouns as groups, the
 * path as its own identity, and "opportunity" for the record. Salesforce shares the
 * groups and the noun.
 */
const UNIFIED_CATALOG_META: CrmCatalogMeta = {
  categoryGroups: getCategoryGroupsFor(OPPORTUNITY_RECORD_COPY.label),
  recordCopy: OPPORTUNITY_RECORD_COPY,
};

// Keyed on `LaunchableCrm` so adding a CRM to `LAUNCHABLE_CRMS` fails to compile until it has an entry.
const CRM_CATALOG_META: Record<LaunchableCrm, CrmCatalogMeta> = {
  [GQL.Integration.HubSpot]: {
    categoryGroups: getCategoryGroupsFor(DEAL_RECORD_COPY.label),
    recordCopy: DEAL_RECORD_COPY,
  },
  [GQL.Integration.SalesForce]: UNIFIED_CATALOG_META,
};

/**
 * Find CRM coverage and serviceIds here => https://developers.apideck.com/apis/crm/coverage.
 */
export function getServiceIdFromIntegration(integration: GQL.Integration): string {
  return INTEGRATION_TO_SERVICE_ID[integration];
}

// Reverse lookup of `getServiceIdFromIntegration`; null for service ids Aline does not model.
export function getIntegrationNameFromServiceId(serviceId: string): GQL.Integration | null {
  return (
    Object.values(GQL.Integration).find(
      integration => INTEGRATION_TO_SERVICE_ID[integration] === serviceId
    ) ?? null
  );
}

export function getIsLaunchableCrm(
  integration: GQL.Integration | null | undefined
): integration is LaunchableCrm {
  return LAUNCHABLE_CRMS.some(launchable => launchable === integration);
}

/**
 * Key two attribute paths to the same string when they read the same CRM field.
 * CRMs without a native catalog, and service ids Aline does not model, have one path
 * per field, so the path is the key.
 */
export function getCrmFieldIdentityKey(
  integration: GQL.Integration | null | undefined
): (path: string) => string {
  if (integration === GQL.Integration.HubSpot) return hubspotFieldIdentityKey;
  if (integration === GQL.Integration.SalesForce) return salesforceFieldIdentityKey;
  return path => path;
}

// Picker group fields for an `IntegrationField`.
export function getIntegrationFieldGroup(field: {
  readonly category: GQL.CrmIntegrationCategory;
  readonly integration: GQL.Integration | null;
}): Pick<GQL.IntegrationField, 'groupDisplayName' | 'groupSortIndex'> {
  const { displayName, sortIndex } = getCrmCatalogMeta(field.integration).categoryGroups[
    field.category
  ];
  return { groupDisplayName: displayName, groupSortIndex: sortIndex };
}

// Copy for the linked CRM record noun: "a deal" for HubSpot, "an opportunity" elsewhere.
export function getCrmRecordCopy(integration: GQL.Integration | null | undefined): CrmRecordCopy {
  return getCrmCatalogMeta(integration).recordCopy;
}

export enum IntegrationSearchAttribute {
  title = 'title',
}

export const integrationToSearchSupport: Record<
  GQL.Integration,
  { supportedFilters: IntegrationSearchAttribute[]; supportedSort: IntegrationSearchAttribute[] }
> = {
  [GQL.Integration.Act]: {
    supportedFilters: [IntegrationSearchAttribute.title],
    supportedSort: [IntegrationSearchAttribute.title],
  },
  [GQL.Integration.ActiveCampaign]: {
    supportedFilters: [IntegrationSearchAttribute.title],
    supportedSort: [IntegrationSearchAttribute.title],
  },
  [GQL.Integration.Close]: { supportedFilters: [], supportedSort: [] },
  [GQL.Integration.Copper]: {
    supportedFilters: [IntegrationSearchAttribute.title],
    supportedSort: [IntegrationSearchAttribute.title],
  },
  [GQL.Integration.HubSpot]: {
    supportedFilters: [IntegrationSearchAttribute.title],
    supportedSort: [IntegrationSearchAttribute.title],
  },
  [GQL.Integration.MicrosoftDynamics]: {
    supportedFilters: [IntegrationSearchAttribute.title],
    supportedSort: [IntegrationSearchAttribute.title],
  },
  [GQL.Integration.Pipedrive]: {
    supportedFilters: [IntegrationSearchAttribute.title],
    supportedSort: [IntegrationSearchAttribute.title],
  },
  [GQL.Integration.Salesflare]: {
    supportedFilters: [IntegrationSearchAttribute.title],
    supportedSort: [IntegrationSearchAttribute.title],
  },
  [GQL.Integration.SalesForce]: {
    supportedFilters: [IntegrationSearchAttribute.title],
    supportedSort: [IntegrationSearchAttribute.title],
  },
  [GQL.Integration.Teamleader]: {
    supportedFilters: [IntegrationSearchAttribute.title],
    supportedSort: [],
  },
  [GQL.Integration.ZendeskSell]: {
    supportedFilters: [IntegrationSearchAttribute.title],
    supportedSort: [IntegrationSearchAttribute.title],
  },
  [GQL.Integration.ZohoCrm]: {
    supportedFilters: [IntegrationSearchAttribute.title],
    supportedSort: [IntegrationSearchAttribute.title],
  },
};

function getCrmCatalogMeta(integration: GQL.Integration | null | undefined) {
  return getIsLaunchableCrm(integration) ? CRM_CATALOG_META[integration] : UNIFIED_CATALOG_META;
}

/**
 * Picker groups in display order. The record noun heads the main group and prefixes the
 * Lead and Owner sub-groups ("Deal - Owner"), so unified opportunity fields land in the
 * same group as a CRM's native record properties. Company is Company everywhere.
 */
function getCategoryGroupsFor(recordLabel: string) {
  return {
    [GQL.CrmIntegrationCategory.Opportunity]: { displayName: recordLabel, sortIndex: 1 },
    [GQL.CrmIntegrationCategory.Company]: { displayName: 'Company', sortIndex: 2 },
    [GQL.CrmIntegrationCategory.Lead]: { displayName: `${recordLabel} - Lead`, sortIndex: 3 },
    [GQL.CrmIntegrationCategory.Owner]: { displayName: `${recordLabel} - Owner`, sortIndex: 4 },
  } satisfies CrmCategoryGroups;
}
